interface InsightTextProps {
  dimensionId?: number;
  municipalityName: string;
  stateName: string;
  language: string;
  comparedMunicipalities?: string[];
}

const formatMunicipalitiesList = (municipalities: string[], language: string): string => {
  if (municipalities.length === 0) {
    return language === 'pt-BR' ? 'os municípios comparados' : 'the municipalities compared';
  }
  if (municipalities.length === 1) {
    return municipalities[0];
  }
  if (municipalities.length === 2) {
    return language === 'pt-BR'
      ? `${municipalities[0]} e ${municipalities[1]}`
      : `${municipalities[0]} and ${municipalities[1]}`;
  }
  const lastMunicipality = municipalities[municipalities.length - 1];
  const otherMunicipalities = municipalities.slice(0, -1);
  return language === 'pt-BR'
    ? `${otherMunicipalities.join(', ')} e ${lastMunicipality}`
    : `${otherMunicipalities.join(', ')}, and ${lastMunicipality}`;
};

const formatMunicipalitiesForComparison = (
  comparedMunicipalities: string[],
  municipalityName: string,
  language: string
): string => {
  const allMunicipalities = [...comparedMunicipalities];
  if (municipalityName && !allMunicipalities.includes(municipalityName)) {
    allMunicipalities.push(municipalityName);
  }
  return formatMunicipalitiesList(allMunicipalities, language);
};

export const generateInsightText = ({
  dimensionId,
  municipalityName,
  comparedMunicipalities = [],
  stateName,
  language,
}: InsightTextProps) => {
  const formattedAllMunicipalities = formatMunicipalitiesForComparison(
    comparedMunicipalities,
    municipalityName,
    language
  );

  if (language === 'pt-BR') {
    switch (dimensionId) {
      case 43:
        return `Como o estado ${stateName} e o município de ${municipalityName} se comparam em termos de Gestão do Uso da Terra?`;
      case 44:
        return `Como o estado ${stateName} e o município de ${municipalityName} se comparam em termos de Clima e Natureza?`;
      case 45:
        return `Como o estado ${stateName} e o município de ${municipalityName} se comparam em termos de Contexto Econômico?`;
      case 46:
        return `Como o estado ${stateName} e o município de ${municipalityName} se comparam em termos de Fibra Social?`;
      case 47:
        return `Como o estado ${stateName} e o município de ${municipalityName} se comparam em termos de Presença Institucional?`;
      case 48:
        return `Como o estado ${stateName} e o município de ${municipalityName} se comparam em termos de Segurança?`;
      case 100:
        return `Como ${formattedAllMunicipalities} se comparam na performance geral?`;
      case 143:
        return `Como ${formattedAllMunicipalities} se comparam em termos de Gestão de Uso do Solo?`;
      case 144:
        return `Como ${formattedAllMunicipalities} se comparam em termos de Clima e Natureza?`;
      case 145:
        return `Como ${formattedAllMunicipalities} se comparam em termos de Contexto Econômico`;
      case 146:
        return `Como ${formattedAllMunicipalities} se comparam em termos de Fibra Social??`;
      case 147:
        return `Como ${formattedAllMunicipalities} se comparam em termos de Presença Institucional??`;
      case 148:
        return `Como ${formattedAllMunicipalities} se comparam em termos de Segurança?`;
      default:
        return comparedMunicipalities.length > 0
          ? `Como ${formattedAllMunicipalities} se comparam na performance geral?`
          : `Como o estado ${stateName} e ${municipalityName} se comparam na performance geral?`;
    }
  }

  switch (dimensionId) {
    case 43:
      return `How do ${municipalityName} and the state of ${stateName}  compare in terms of Land Use Management?`;
    case 44:
      return `How do ${municipalityName} and the state of ${stateName} compare in terms of Climate and Nature?`;
    case 45:
      return `How do ${municipalityName} and the state of ${stateName} compare in terms of Economic Context?`;
    case 46:
      return `How do ${municipalityName} and the state of ${stateName} compare in terms of Social Fabric?`;
    case 47:
      return `How do ${municipalityName} and the state of ${stateName} compare in terms of Institutional Presence?`;
    case 48:
      return `How do ${municipalityName} and the state of ${stateName} compare in terms of Security?`;
    case 100:
      return `How do ${formattedAllMunicipalities} compare in overall performance?`;
    case 143:
      return `How do ${formattedAllMunicipalities} compare in Land Use Management?`;
    case 144:
      return `How do ${formattedAllMunicipalities} compare in Climate and Nature?`;
    case 145:
      return `How do ${formattedAllMunicipalities} compare in Economic Context?`;
    case 146:
      return `How do ${formattedAllMunicipalities} compare in Social Fabric?`;
    case 147:
      return `How do ${formattedAllMunicipalities} compare in Institutional Presence?`;
    case 148:
      return `How do ${formattedAllMunicipalities} compare in Security?`;
    default:
      return comparedMunicipalities.length > 0
        ? `How do ${formattedAllMunicipalities} compare in overall performance?`
        : `How does the municipality of ${municipalityName} compare to the state of ${stateName} in overall performance?`;
  }
};
